/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
export type AccidentalPreference = 'sharps' | 'flats';

export interface KeyOption {
  sharp: string;
  flat?: string;
}

export const keyOptions: KeyOption[] = [
  { sharp: 'C', flat: 'C' },
  { sharp: 'C#', flat: 'Db' },
  { sharp: 'D', flat: 'D' },
  { sharp: 'D#', flat: 'Eb' },
  { sharp: 'E', flat: 'E' },
  { sharp: 'F', flat: 'F' },
  { sharp: 'F#', flat: 'Gb' },
  { sharp: 'G', flat: 'G' },
  { sharp: 'G#', flat: 'Ab' },
  { sharp: 'A', flat: 'A' },
  { sharp: 'A#', flat: 'Bb' },
  { sharp: 'B', flat: 'B' }
];

export const findKeyOption = (value: string): KeyOption | undefined =>
  keyOptions.find(
    option =>
      option.sharp.toLowerCase() === value.toLowerCase() ||
      option.flat?.toLowerCase() === value.toLowerCase()
  );

export const getPreferredKeyValue = (value: string, preference: AccidentalPreference): string => {
  const option = findKeyOption(value);
  if (!option) {
    return value;
  }
  if (preference === 'flats' && option.flat) {
    return option.flat;
  }
  return option.sharp;
};

export const getDisplayKeyOptions = (preference: AccidentalPreference): string[] =>
  keyOptions.map(option => (preference === 'flats' && option.flat ? option.flat : option.sharp));

export const chordOptions = [
  { display: 'Major', value: 'major' },
  { display: 'Minor', value: 'minor' },
  { display: 'Augmented', value: 'augmented' },
  { display: 'Diminished', value: 'diminished' },
  { display: 'Sus4', value: 'sus4' },
  { display: 'Sus2', value: 'sus2' },
  { display: 'Major 7', value: 'major7' },
  { display: 'Minor 7', value: 'minor7' },
  { display: '7 (Dominant 7)', value: 'dominant7' },
  { display: 'Minor 7♭5', value: 'm7b5' },
  { display: 'Diminished 7', value: 'dim7' },
  { display: '6', value: '6' },
  { display: 'Minor 6', value: 'minor6' },
  { display: 'Major 9', value: 'major9' },
  { display: 'Minor 9', value: 'minor9' },
  { display: '9', value: '9' },
  { display: 'Minor 11', value: 'minor11' },
  { display: 'Major 11', value: 'major11' },
  { display: 'Minor 13', value: 'minor13' },
  { display: 'Major 13', value: 'major13' },
  { display: 'Augmented 7', value: 'augmented7' },
  { display: 'Augmented Major 7', value: 'augmentedmajor7' },
  { display: 'Minor/Major 7', value: 'minormajor7' },
  { display: '7sus4', value: '7sus4' },
  { display: '9sus4', value: '9sus4' },
  { display: '13sus4', value: '13sus4' },
  { display: '7♯5', value: '7#5' },
  { display: '7♭9', value: '7b9' },
  { display: '7♯11', value: '7#11' },
  { display: '13♭9', value: '13b9' },
  { display: 'Major(add9)', value: 'majoradd9' },
  { display: 'Add11', value: 'add11' },
  { display: '7♯9 (Hendrix)', value: '7#9' },
  { display: '5 (Power Chord)', value: 'powerchord' }
];

export const scaleOptions = [
  { display: 'Ionian (Major)', value: 'ionian' },
  { display: 'Dorian', value: 'dorian' },
  { display: 'Phrygian', value: 'phrygian' },
  { display: 'Lydian', value: 'lydian' },
  { display: 'Mixolydian', value: 'mixolydian' },
  { display: 'Aeolian (Minor)', value: 'aeolian' },
  { display: 'Locrian', value: 'locrian' },
  { display: 'Harmonic Minor', value: 'harmonicminor' },
  { display: 'Melodic Minor', value: 'melodicminor' },
  { display: 'Pentatonic Major', value: 'pentatonicmajor' },
  { display: 'Pentatonic Minor', value: 'pentatonicminor' },
  { display: 'Blues Minor', value: 'bluesminor' },
  { display: 'Blues Major', value: 'bluesmajor' }
];

// ==========================================
// SUBSCRIPTION TIER FEATURE CONFIGURATION
// ==========================================

/**
 * Free tier: C key only with core scales
 * Includes pentatonics (essential for intermediate guitarists)
 */
export const FREE_TIER_ALLOWED_SCALES = [
  'ionian',           // Major
  'aeolian',          // Minor
  'pentatonicmajor',  // Essential for soloing
  'pentatonicminor'   // Essential for blues/rock
];

/**
 * Free tier: Basic chord types only
 */
export const FREE_TIER_ALLOWED_CHORDS = ['major', 'minor'];

/**
 * Free tier: C key only
 */
export const FREE_TIER_ALLOWED_KEYS = ['C'];

/**
 * Check if a scale/mode is available for Free tier users
 */
export const isScaleAvailableForFree = (scaleValue: string): boolean => {
  return FREE_TIER_ALLOWED_SCALES.includes(scaleValue);
};

/**
 * Check if a chord type is available for Free tier users
 */
export const isChordAvailableForFree = (chordValue: string): boolean => {
  return FREE_TIER_ALLOWED_CHORDS.includes(chordValue);
};

/**
 * Check if a key is available for Free tier users
 */
export const isKeyAvailableForFree = (keyValue: string): boolean => {
  return FREE_TIER_ALLOWED_KEYS.includes(keyValue);
};
